import React from "react";
import { createPortal } from "react-dom";

export type ReorderGhost = {
  html: string;
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
  width: number;
};

const indexFromPointer = (selector: string, clientY: number, scope?: ParentNode | null): number | null => {
  const items = Array.from((scope ?? document).querySelectorAll(selector));
  if (items.length === 0) return null;
  for (let i = 0; i < items.length; i++) {
    const rect = items[i].getBoundingClientRect();
    if (clientY < rect.top + rect.height / 2) return i;
  }
  return items.length - 1;
};

export const ReorderGhostOverlay: React.FC<{ ghost: ReorderGhost | null }> = ({ ghost }) => {
  if (!ghost) return null;
  return createPortal(
    <div
      className="pointer-events-none fixed z-2000 rotate-[1deg] [&>*]:shadow-2xl"
      style={{
        left: ghost.x - ghost.offsetX,
        top: ghost.y - ghost.offsetY,
        width: ghost.width,
      }}
      dangerouslySetInnerHTML={{ __html: ghost.html }}
    />,
    document.body
  );
};

export const beginPointerReorder = ({
  event,
  card,
  index,
  itemSelector,
  scope,
  onReorder,
  onActiveChange,
  onGhostChange,
}: {
  event: React.PointerEvent;
  card: HTMLElement;
  index: number;
  itemSelector: string;
  scope?: ParentNode | null;
  onReorder: (from: number, to: number) => void;
  onActiveChange: (index: number | null) => void;
  onGhostChange: (ghost: ReorderGhost | null) => void;
}) => {
  if (event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();

  const rect = card.getBoundingClientRect();
  const html = card.outerHTML;
  const offsetX = event.clientX - rect.left;
  const offsetY = event.clientY - rect.top;
  let from = index;
  const previousUserSelect = document.body.style.userSelect;
  document.body.style.userSelect = "none";
  onActiveChange(index);
  onGhostChange({
    html,
    x: event.clientX,
    y: event.clientY,
    offsetX,
    offsetY,
    width: rect.width,
  });

  const onMove = (next: PointerEvent) => {
    onGhostChange({
      html,
      x: next.clientX,
      y: next.clientY,
      offsetX,
      offsetY,
      width: rect.width,
    });

    const to = indexFromPointer(itemSelector, next.clientY, scope);
    if (to == null || to === from) return;
    onReorder(from, to);
    from = to;
    onActiveChange(to);
  };

  const onUp = () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    document.body.style.userSelect = previousUserSelect;
    onActiveChange(null);
    onGhostChange(null);
  };

  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
};
